import { Theme } from '@mui/system';
import validators from 'helpers/validators';
import { TFunction } from 'i18next';
import React from 'react';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';

type NavLinkFormProps = {
    text: string;
    url: string;
    fontColor: string;
    backgroundColor: string;
    errors: {
        text?: string;
        url?: string;
        fontColor?: string;
        backgroundColor?: string;
    };
    handleTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleUrlChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleFontColorChange: (value: string) => void;
    handleBackgroundColorChange: (value: string) => void;
    validateFields: () => boolean;
};

type NavLinkFormErrorsT = {
    text?: string;
    url?: string;
    fontColor?: string;
    backgroundColor?: string;
};

/**
 * Handles the change event for text and URL inputs.
 */
const handleChange =
    (
        setter: SetState<string>,
        field: string,
        setErrors: SetState<NavLinkFormErrorsT>,
    ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
        setter(event.target.value);
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

/**
 * Handles the change event for color inputs.
 */
const handleColorChange =
    (
        setter: SetState<string>,
        field: string,
        setErrors: SetState<NavLinkFormErrorsT>,
    ) =>
    (value: string) => {
        setter(value);
        setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

/**
 * Custom hook to manage the state and validation of the EditNavLink form.
 * @returns Contains form values, error messages, and handlers for input changes.
 */
export const useEditNavLinkForm = (
    widget: WidgetT,
    t: TFunction,
    theme: Theme,
): NavLinkFormProps => {
    const [text, setText] = React.useState(widget.content?.text || '');
    const [url, setUrl] = React.useState(widget.content?.url || '');
    const [fontColor, setFontColor] = React.useState(
        widget.content?.fontColor || theme.palette.darkGrey || '',
    );
    const [backgroundColor, setBackgroundColor] = React.useState(
        widget.content?.backgroundColor || theme.palette.common.white,
    );
    const [errors, setErrors] = React.useState<NavLinkFormErrorsT>({});

    const validateFields = () => {
        const newErrors: Record<string, string> = {};
        if (!validators.required(text))
            newErrors.text = t('validators.required');
        if (!validators.required(url)) newErrors.url = t('validators.required');
        else if (!validators.isUrl(url)) newErrors.url = t('validators.isUrl');
        if (!validators.required(fontColor))
            newErrors.fontColor = t('validators.required');
        if (!validators.required(backgroundColor))
            newErrors.backgroundColor = t('validators.required');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    return {
        text,
        url,
        fontColor,
        backgroundColor,
        errors,
        handleTextChange: handleChange(setText, 'text', setErrors),
        handleUrlChange: handleChange(setUrl, 'url', setErrors),
        handleFontColorChange: handleColorChange(
            setFontColor,
            'fontColor',
            setErrors,
        ),
        handleBackgroundColorChange: handleColorChange(
            setBackgroundColor,
            'backgroundColor',
            setErrors,
        ),
        validateFields,
    };
};
